import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { downloadKeepsakePdf } from "@/components/KeepsakesView";
import { CaretLeft, DownloadSimple, Printer, BookOpen } from "@phosphor-icons/react";

export default function KeepsakeViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [k, setK] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/keepsakes/${id}`)
      .then((r) => setK(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  const download = async () => {
    try { await downloadKeepsakePdf(id, k?.title); }
    catch { toast.error("Download failed. Please try again."); }
  };

  if (notFound) {
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <h1 className="font-serif text-3xl text-[#2C2A28]">Keepsake not found</h1>
        <Button onClick={() => navigate("/timeline")} className="mt-6 rounded-full bg-[#B08D57] text-[#FDFBF7]">
          Back to my journey
        </Button>
      </div>
    );
  }
  if (!k) return <p className="px-6 py-24 text-center text-[#8A7F76]">Opening your keepsake…</p>;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Toolbar — hidden when printing */}
      <div className="no-print sticky top-16 z-10 border-b border-[#4A4540]/10 bg-[#FDFBF7]/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-3">
          <Button data-testid="keepsake-back-btn" onClick={() => navigate("/timeline")} variant="ghost" className="text-[#8A7F76] hover:bg-[#F4EFE6]">
            <CaretLeft size={18} className="mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <Button data-testid="keepsake-print-btn" onClick={() => window.print()} variant="outline" className="rounded-full border-[#4A4540]/20 text-[#2C2A28]">
              <Printer size={16} weight="duotone" className="mr-1" /> Print
            </Button>
            <Button data-testid="keepsake-download-btn" onClick={download} className="rounded-full bg-[#B08D57] text-[#FDFBF7] hover:bg-[#C19A6B]">
              <DownloadSimple size={16} weight="duotone" className="mr-1" /> Download PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Book */}
      <article data-testid="keepsake-content" className="mx-auto max-w-2xl px-6 py-14 print:py-0">
        <div className="mb-14 text-center">
          <BookOpen size={30} weight="duotone" className="mx-auto text-[#B08D57] no-print" />
          <h1 className="mt-5 font-serif text-4xl leading-tight text-[#2C2A28] sm:text-5xl">{k.title}</h1>
          <p className="mt-4 font-handwriting text-2xl text-[#B08D57]">Your Life Journeys Matter</p>
        </div>

        {k.dedication && (
          <p data-testid="keepsake-dedication" className="mb-10 whitespace-pre-line text-center font-serif text-xl italic leading-relaxed text-[#4A4540]">
            {k.dedication}
          </p>
        )}

        {k.intro && (
          <p className="mb-10 whitespace-pre-line font-serif text-lg italic leading-relaxed text-[#4A4540]">
            {k.intro}
          </p>
        )}

        {k.sections.map((s, i) => (
          <section key={i} className="mb-10 break-inside-avoid">
            <h2 className="mb-3 font-serif text-2xl text-[#B08D57]">{s.heading}</h2>
            <p className="whitespace-pre-line text-[17px] leading-[1.8] text-[#2C2A28]">{s.body}</p>
          </section>
        ))}

        <p className="mt-16 text-center text-sm text-[#8A7F76] no-print">
          Crafted from your own memories · private to you
        </p>
      </article>
    </div>
  );
}
